// /support/:ticketNumber — ticket detail + reply + (staff) status form.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type {
  AdminSupportCategory,
  StaffMember,
  SupportTicketDetail,
  TicketPriority,
  TicketStatus,
} from '../types';

const STATUSES: TicketStatus[] = ['OPEN', 'PENDING', 'WAITING_ON_CUSTOMER', 'RESOLVED', 'CLOSED'];
const PRIORITIES: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const STATUS_BADGE: Record<TicketStatus, string> = {
  OPEN: 'badge-open',
  PENDING: 'badge-pending',
  WAITING_ON_CUSTOMER: 'badge-waiting',
  RESOLVED: 'badge-resolved',
  CLOSED: 'badge-closed',
};
const PRIORITY_BADGE: Record<TicketPriority, string> = {
  LOW: 'badge-prio-low',
  MEDIUM: 'badge-prio-medium',
  HIGH: 'badge-prio-high',
  URGENT: 'badge-prio-urgent',
};
const STATUS_LABEL: Record<TicketStatus, string> = {
  OPEN: 'Open',
  PENDING: 'Pending',
  WAITING_ON_CUSTOMER: 'Waiting on customer',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

export function SupportTicketPage() {
  const { ticketNumber = '' } = useParams();
  const { user } = useAuth();
  const isStaff = Boolean(user?.isStaff);

  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Staff-only side panel state
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [adminCats, setAdminCats] = useState<AdminSupportCategory[]>([]);

  const reload = useCallback(async () => {
    try {
      const t = await api<SupportTicketDetail>(`/api/support/tickets/${encodeURIComponent(ticketNumber)}`);
      setTicket(t);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  }, [ticketNumber]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (!isStaff) return;
    void (async () => {
      try {
        const [s, c] = await Promise.all([
          api<StaffMember[]>('/api/support/tickets/_helpers/staff'),
          api<AdminSupportCategory[]>('/api/support/categories/_admin'),
        ]);
        setStaff(s);
        setAdminCats(c);
      } catch {
        /* non-fatal */
      }
    })();
  }, [isStaff]);

  async function onSubmitReply(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!ticket) return;
    setSubmitting(true);
    try {
      await api(`/api/support/tickets/${encodeURIComponent(ticket.ticketNumber)}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message: reply, isInternalNote: internalNote }),
      });
      setReply('');
      setInternalNote(false);
      await reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Reply failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function patchTicket(patch: Record<string, unknown>) {
    if (!ticket) return;
    try {
      await api(`/api/support/tickets/${encodeURIComponent(ticket.ticketNumber)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      await reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Update failed');
    }
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="form-error">{error}</div>
        <Link to="/support" className="btn-link">← Back to tickets</Link>
      </div>
    );
  }
  if (!ticket) {
    return <div className="page-content"><p style={{ color: 'var(--text-muted)' }}>Loading…</p></div>;
  }

  return (
    <div className="page-content">
      <Link to="/support" className="btn-link" style={{ padding: 0, marginBottom: 8 }}>← All tickets</Link>
      <header className="ticket-header">
        <div>
          <h1>{ticket.subject}</h1>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            {ticket.ticketNumber} · {ticket.category.name} ·{' '}
            <span className={`badge ${STATUS_BADGE[ticket.status]}`}>{STATUS_LABEL[ticket.status]}</span>{' '}
            <span className={`badge ${PRIORITY_BADGE[ticket.priority]}`}>{ticket.priority.toLowerCase()}</span>
          </p>
          {ticket.requester && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '4px 0 0' }}>
              Opened by {ticket.requester.name} ({ticket.requester.email}) · {new Date(ticket.createdAt).toLocaleString()}
            </p>
          )}
        </div>
        {ticket.assignedTo && (
          <span className="badge badge-active">Assigned: {ticket.assignedTo.name}</span>
        )}
      </header>

      <div className="ticket-body">
        <div className="ticket-thread">
          {ticket.messages.map((m) => (
            <article
              key={m.id}
              className={`ticket-message${m.isStaffReply ? ' is-staff' : ''}${m.isInternalNote ? ' is-internal' : ''}`}
            >
              <header>
                <strong>{m.author.name}</strong>
                {m.isStaffReply && <span className="badge badge-active" style={{ marginLeft: 8 }}>Staff</span>}
                {m.isInternalNote && <span className="badge badge-prio-medium" style={{ marginLeft: 8 }}>Internal note</span>}
                <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
                  {new Date(m.createdAt).toLocaleString()}
                </span>
              </header>
              <p>{m.message}</p>
            </article>
          ))}

          {ticket.status !== 'CLOSED' && (
            <form onSubmit={(e) => void onSubmitReply(e)} className="admin-form" style={{ marginTop: 16 }}>
              <div className="form-group">
                <label>{isStaff ? 'Reply / note' : 'Reply'}</label>
                <textarea
                  className="admin-textarea"
                  rows={5}
                  required
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  disabled={submitting}
                  placeholder="Type your message…"
                />
              </div>
              {isStaff && (
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={internalNote}
                    onChange={(e) => setInternalNote(e.target.checked)}
                    disabled={submitting}
                  />
                  Internal note (staff only — not emailed to the user)
                </label>
              )}
              <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="submit" className="btn-primary" disabled={submitting || !reply.trim()}>
                  {submitting ? 'Sending…' : 'Send'}
                </button>
                {!isStaff && ticket.status !== 'RESOLVED' && (
                  <button type="button" className="btn-secondary" onClick={() => void patchTicket({ status: 'RESOLVED' })}>
                    Mark resolved
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {isStaff && (
          <aside className="ticket-sidebar">
            <h3>Manage</h3>

            <div className="form-group">
              <label>Status</label>
              <select
                className="admin-select"
                value={ticket.status}
                onChange={(e) => void patchTicket({ status: e.target.value })}
              >
                {STATUSES.map((s) => (<option key={s} value={s}>{STATUS_LABEL[s]}</option>))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select
                className="admin-select"
                value={ticket.priority}
                onChange={(e) => void patchTicket({ priority: e.target.value })}
              >
                {PRIORITIES.map((p) => (<option key={p} value={p}>{p.toLowerCase()}</option>))}
              </select>
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                className="admin-select"
                value={ticket.category.id}
                onChange={(e) => void patchTicket({ categoryId: e.target.value })}
              >
                {adminCats.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            </div>

            <div className="form-group">
              <label>Assigned to</label>
              <select
                className="admin-select"
                value={ticket.assignedTo?.id ?? ''}
                onChange={(e) => void patchTicket({ assignedToId: e.target.value || null })}
              >
                <option value="">— unassigned —</option>
                {staff.map((s) => (<option key={s.id} value={s.id}>{s.name} ({s.role})</option>))}
              </select>
            </div>

            {(ticket.slaResponseDue || ticket.slaResolveDue) && (
              <div className="form-group" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <strong style={{ display: 'block', marginBottom: 4, color: 'var(--text-secondary)' }}>SLA</strong>
                {ticket.slaResponseDue && (
                  <div>First response: {new Date(ticket.slaResponseDue).toLocaleString()}{ticket.firstResponseAt && ` · met ✓`}</div>
                )}
                {ticket.slaResolveDue && (
                  <div>Resolution: {new Date(ticket.slaResolveDue).toLocaleString()}{ticket.closedAt && ` · closed`}</div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
